// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { InventoryEntry, getTotalCount } from '@/inventory/api/types';
import { InventoryDimension } from '@/inventory/hooks';
import { InventoryChildRow } from '@/items/components/inventory-child-row';
import { InventoryHeaderRow } from '@/items/components/inventory-header-row';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import {
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface GroupedItemInventoryItem {
  item: SparseItem;
  locations: {
    location: SparseLocation;
    inventory: InventoryEntry[];
  }[];
}

interface GroupedLocationInventoryItem {
  location: SparseLocation;
  items: {
    bulkItems: {
      item: SparseItem;
      location: SparseLocation;
      inventory: InventoryEntry[];
    }[];
  };
}

type InventoryItem = GroupedItemInventoryItem | GroupedLocationInventoryItem;

interface Props {
  view: InventoryDimension;
  data: InventoryItem[];
  isLoading?: boolean;
  isFetching?: boolean;
}

function InventoryTableHeader(props: { view: InventoryDimension }) {
  return (
    <TableHead>
      <TableRow>
        <TableCell
          width="55%"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {props.view === 'item' ? 'SKU' : 'Location'}
        </TableCell>
        <TableCell
          width="20%"
          align="right"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Total on hand
        </TableCell>
        <TableCell
          width="25%"
          align="right"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

export function InventoryTable(props: Props) {
  const { data, isLoading, isFetching, view } = props;
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandedZones, setExpandedZones] = useState<Record<string, boolean>>(
    {}
  );
  const [expandedLots, setExpandedLots] = useState<Record<string, boolean>>({});

  const toggleRowExpand = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleZoneExpand = (zoneKey: string) => {
    setExpandedZones((prev) => ({
      ...prev,
      [zoneKey]: !prev[zoneKey],
    }));
  };

  const toggleLotExpand = (lotKey: string) => {
    setExpandedLots((prev) => ({
      ...prev,
      [lotKey]: !prev[lotKey],
    }));
  };

  if (isLoading) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <InventoryTableHeader view={view} />
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton variant="text" width="60%" />
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="text" width="40%" />
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="rounded" width={80} height={32} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState title="No inventory found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            To start tracking inventory in Hardfin, add items to locations
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      {isFetching && <LinearProgress />}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <InventoryTableHeader view={view} />
          <TableBody>
            {data.map((row) => {
              const {
                isExpanded,
                totalQuantity,
                rowId,
                rowHeader,
                rowSubheader = undefined,
              } = view === 'item'
                ? (() => {
                    const itemRow = row as GroupedItemInventoryItem;
                    return {
                      isExpanded: expandedRows[itemRow.item.key],
                      totalQuantity: itemRow.locations
                        .map((row) => getTotalCount(row.inventory))
                        .reduce((sum, current) => sum + current, 0),
                      rowId: itemRow.item.key,
                      rowHeader: itemRow.item.sku,
                      rowSubheader: itemRow.item.name,
                    };
                  })()
                : (() => {
                    const locationRow = row as GroupedLocationInventoryItem;
                    return {
                      isExpanded: expandedRows[locationRow.location.key],
                      totalQuantity: locationRow.items.bulkItems
                        .map((row) => getTotalCount(row.inventory))
                        .reduce((sum, current) => sum + current, 0),
                      rowId: locationRow.location.key,
                      rowHeader: getEffectiveLocationName(locationRow.location),
                      rowSubheader: undefined,
                    };
                  })();

              return (
                <InventoryHeaderRow
                  key={rowId}
                  view={props.view}
                  expanded={isExpanded}
                  onToggleExpand={() => toggleRowExpand(rowId)}
                  hasChildren={totalQuantity > 0}
                  name={rowHeader}
                  secondaryText={rowSubheader}
                  totalQuantity={totalQuantity}
                  location={
                    view === 'location'
                      ? (row as GroupedLocationInventoryItem).location
                      : undefined
                  }
                  item={
                    view === 'item'
                      ? (row as GroupedItemInventoryItem).item
                      : undefined
                  }
                  hideActions={
                    view === 'location' &&
                    (row as GroupedLocationInventoryItem).location.zoneCount > 0
                  }
                >
                  <InventoryChildRow
                    view={props.view}
                    expanded={isExpanded}
                    itemRow={
                      props.view === 'item'
                        ? (row as GroupedItemInventoryItem)
                        : undefined
                    }
                    locationRow={
                      props.view === 'location'
                        ? (row as GroupedLocationInventoryItem)
                        : undefined
                    }
                    expandedZones={expandedZones}
                    onToggleZoneExpand={toggleZoneExpand}
                    expandedLots={expandedLots}
                    onToggleLotExpand={toggleLotExpand}
                  />
                </InventoryHeaderRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
